import { useMemo } from "react";
import { useShallow } from "zustand/shallow";
import { useUserStore } from "../user/state/store";
import {
  SuperuserSupportAccount,
  useSuperuserContextStore,
} from "./state/superuserContext";

type UserSnapshot = ReturnType<typeof useUserStore.getState>;

function applySupportAccount(
  user: UserSnapshot,
  supportOrgActive: boolean,
  selectedAccount: SuperuserSupportAccount | null,
): UserSnapshot {
  if (!supportOrgActive) return user;
  if (!selectedAccount) {
    return {
      ...user,
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      isOwner: false,
      isAdmin: false,
      isOperator: false,
      isContact: false,
      canViewCalendar: false,
      isSuperuser: false,
      viewMode: "org",
    };
  }

  const isMember = selectedAccount.source === "member";
  const role = selectedAccount.role.toLowerCase();

  return {
    ...user,
    id: selectedAccount.id,
    email: selectedAccount.email,
    firstName: selectedAccount.label,
    lastName: "",
    isOwner: isMember && role === "owner",
    isAdmin: isMember && role === "admin",
    isOperator: isMember && role === "operator",
    isContact: selectedAccount.source === "location_contact",
    // Organization support requests have calendar-manager access on the server.
    canViewCalendar: isMember,
    isSuperuser: false,
    viewMode: selectedAccount.source === "location_contact" ? "contact" : "org",
  };
}

export function getEffectiveVendUserSnapshot(): UserSnapshot {
  const user = useUserStore.getState();
  const superuser = useSuperuserContextStore.getState();
  return applySupportAccount(
    user,
    superuser.enabled && !!superuser.selectedOrg,
    superuser.selectedAccount,
  );
}

export function useEffectiveVendUser(): UserSnapshot {
  const user = useUserStore(useShallow((state) => state));
  const { supportOrgActive, selectedAccount } = useSuperuserContextStore(
    useShallow((state) => ({
      supportOrgActive: state.enabled && !!state.selectedOrg,
      selectedAccount: state.selectedAccount,
    })),
  );

  return useMemo(
    () => applySupportAccount(user, supportOrgActive, selectedAccount),
    [selectedAccount, supportOrgActive, user],
  );
}

export function getEffectiveVendViewMode(): string {
  return getEffectiveVendUserSnapshot().viewMode || "org";
}
